import { useEffect } from 'react'
import NoSleep from 'nosleep.js'
import { isApp, isMobile } from '@/helpers'
import { logger } from '@/logger/logger'

const noSleep = new NoSleep()

// Both nosleep.js backends need a user activation to start: the Wake Lock API
// rejects the request without one, and the <video> fallback (Firefox, older iOS
// Safari) is blocked by the autoplay policy. Desktop screens don't sleep during
// play and the app handles its own wake lock, so only mobile browsers need this.
const isSupported = () => !isApp() && isMobile

const hasUserActivated = () => (navigator as Navigator & {
  userActivation?: { hasBeenActive: boolean }
}).userActivation?.hasBeenActive

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const

/** Keeps the display awake while `enabled`, deferring until the first user interaction. */
export const useNoSleep = (enabled: boolean) => {
  useEffect(() => {
    if (!isSupported()) return

    if (!enabled) {
      noSleep.disable()
      return
    }

    if (noSleep.isEnabled) return

    const enable = async () => {
      try {
        if (!noSleep.isEnabled) await noSleep.enable()
      } catch (error) {
        logger.warn('NoSleep error', error)
      }
    }

    if (hasUserActivated()) {
      void enable()
      return
    }

    const onFirstInteraction = () => {
      INTERACTION_EVENTS.forEach(event => window.removeEventListener(event, onFirstInteraction))
      void enable()
    }

    INTERACTION_EVENTS.forEach(event => window.addEventListener(event, onFirstInteraction, { passive: true }))
    return () => INTERACTION_EVENTS.forEach(event => window.removeEventListener(event, onFirstInteraction))
  }, [enabled])
}
